/**
 * RepsDualPage — Sales Manager dual-tab Performance view (/sales/reps).
 *
 *   Personal tab: GET /role-kpis/sales/rep-performance?period…
 *   Team tab:     GET /role-kpis/sales/rep-team?period…
 *
 * 2026-10-07 (Manak, Mamta's screen): every Personal tile read "—". The page
 * used /sales/quota-attainment, whose only field it read was a collected-
 * invoice ÷ RUPEE-target ratio (null for anyone whose targets are in litres),
 * and hard-coded every other tile blank. The Team tab showed won ₹ only.
 *
 * Both endpoints are built from the SAME report as "My Performance"
 * (backend scorecard-live.routes.ts → rep-performance.service.ts), so quota
 * attainment, won sales and the activity score here equal the scorecard's
 * for the same period. A missing target reads "No quota set — ask admin".
 *
 * Tab + period live in the URL (?tab=personal|team&period=…&from=&to=).
 */
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageFrame,
  Tabs,
  Kpi,
  DataTable,
  Progress,
  Spark,
  Avatar,
  LoadingBlock,
  ErrorBlock,
  EmptyBlock,
  useFetch,
  compactINR,
  type DataTableRow,
} from '@/components/executive/ExecAtoms';
import { PeriodPicker, usePeriodParams, withPeriod } from '@/components/scorecard/PeriodPicker';

interface RepPersonal {
  period: { label: string };
  quota: {
    attainmentPct: number | null;
    reason: string | null;
    targets: Array<{ label: string; actual: number; target: number; unit: string | null; note?: string }>;
  };
  closedWon: { rupees: number; count: number };
  pipeline: { count: number; value: number; unvalued: number };
  activityScore: { score: number | null; band: string | null; kpisUsed: number; kpisTotal: number; reason: string | null };
  activity: { callsMade: number; meetingsHeld: number; siteVisits: number; dealerVisits: number };
  trend: { calls: number[]; meetings: number[]; visits: number[] };
}

interface RepTeamRow {
  userId: string;
  name: string;
  roleLabel: string | null;
  openLeads: number;
  wonRupees: number;
  quotaPct: number | null;
  quotaReason: string | null;
  callsMade: number;
  score: number | null;
  kpisUsed: number;
  kpisTotal: number;
}

const TABS = ['Team', 'Personal'];

const fmtQty = (n: number, unit: string | null) =>
  unit === 'L' ? `${Math.round(n).toLocaleString('en-IN')} L` : compactINR(n);

function TrendCell({ label, value, values, color, sub }: { label: string; value: number; values: number[]; color: string; sub?: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: 'var(--ff-serif)', fontSize: 24, fontWeight: 500, color: 'var(--ink-0)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{sub}</div>}
      {values.length > 1 && <Spark values={values} w={160} h={32} color={color} />}
    </div>
  );
}

export function RepsDualPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab')?.toLowerCase();
  const tab = rawTab === 'personal' ? 'Personal' : 'Team';
  const setTab = useCallback(
    (t: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', t.toLowerCase());
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  const periodState = usePeriodParams();
  const pq = periodState.query;

  const personal = useFetch<RepPersonal>(
    tab === 'Personal' && pq !== null ? withPeriod('/role-kpis/sales/rep-performance', pq) : null,
  );
  const team = useFetch<{ rows: RepTeamRow[]; period: { label: string } }>(
    tab === 'Team' && pq !== null ? withPeriod('/role-kpis/sales/rep-team', pq) : null,
  );

  const loading = tab === 'Personal' ? personal.loading : team.loading;
  const err = tab === 'Personal' ? personal.error : team.error;
  const p = personal.data;
  const rangeLabel = (tab === 'Personal' ? p?.period?.label : team.data?.period?.label) ?? null;

  return (
    <PageFrame title="Performance" sub="Quota attainment · pipeline · team leaderboard">
      <Tabs tabs={TABS} active={tab} onChange={setTab} />
      <div style={{ margin: '10px 0 14px' }}>
        <PeriodPicker state={periodState} rangeLabel={rangeLabel} />
      </div>

      {loading && <LoadingBlock />}
      {err && err !== 'not-available' && <ErrorBlock message="Could not load performance data." />}
      {err === 'not-available' && <EmptyBlock message="Performance endpoint not available yet." />}

      {!loading && !err && tab === 'Personal' && p && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
          <Kpi
            label="Quota attainment"
            value={p.quota.attainmentPct != null ? `${p.quota.attainmentPct}%` : p.quota.reason ?? 'No quota set — ask admin'}
            delta={
              p.quota.targets.length
                ? p.quota.targets
                    .map((t) => (t.note ? `${t.label}: ${t.note}` : `${t.label}: ${fmtQty(t.actual, t.unit)} of ${fmtQty(t.target, t.unit)}`))
                    .join(' · ')
                : undefined
            }
            color={p.quota.attainmentPct != null ? 'var(--success)' : 'var(--ink-2)'}
          />
          <Kpi
            label="Open pipeline (now)"
            value={compactINR(p.pipeline.value)}
            delta={`${p.pipeline.count} open leads`}
          />
          <Kpi
            label="Closed won (period)"
            value={compactINR(p.closedWon.rupees)}
            delta={`${p.closedWon.count} won ${p.closedWon.count === 1 ? 'lead' : 'leads'}`}
            color="var(--success)"
          />
          <Kpi
            label="Activity score"
            value={p.activityScore.score != null ? String(Math.round(p.activityScore.score)) : p.activityScore.reason ?? 'No score yet'}
            delta={p.activityScore.kpisTotal ? `${p.activityScore.kpisUsed} of ${p.activityScore.kpisTotal} KPIs · ${p.activityScore.band ?? 'Not scored'}` : undefined}
          />

          <div className="card" style={{ padding: 14, gridColumn: '1 / -1' }}>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: 0.06, marginBottom: 12 }}>
              Activity · weekly trend
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14 }}>
              <TrendCell label="Calls made" value={p.activity.callsMade} values={p.trend.calls} color="var(--ink-0)" />
              <TrendCell label="Meetings held" value={p.activity.meetingsHeld} values={p.trend.meetings} color="var(--saffron-600)" />
              <TrendCell
                label="Site visits"
                value={p.activity.siteVisits}
                values={p.trend.visits}
                color="var(--success)"
                sub={p.activity.dealerVisits ? `+ ${p.activity.dealerVisits} dealer visits` : undefined}
              />
            </div>
          </div>
        </div>
      )}

      {!loading && !err && tab === 'Team' && team.data && (
        <>
          {team.data.rows.length === 0 ? (
            <EmptyBlock message="No one in your team has a KPI set yet." />
          ) : (
            <DataTable
              columns={[
                { k: 'n', label: 'Rep', w: '220px' },
                { k: 'd', label: 'Open leads', w: '100px', align: 'right', mono: true, muted: true },
                { k: 'won', label: 'Won (period)', w: '120px', align: 'right', mono: true },
                { k: 'quota', label: 'Quota', w: '200px' },
                { k: 'calls', label: 'Calls made', w: '100px', align: 'right', mono: true, muted: true },
                { k: 'score', label: 'Score', w: '80px', align: 'right', mono: true },
              ]}
              rows={team.data.rows.map((r): DataTableRow => ({
                n: () => (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <Avatar name={r.name} size={26} />
                    <span>{r.name}</span>
                  </div>
                ),
                d: r.openLeads,
                won: compactINR(r.wonRupees),
                quota: () =>
                  r.quotaPct == null ? (
                    <span style={{ fontSize: 11, color: 'var(--ink-3)' }} title={r.quotaReason ?? undefined}>
                      No quota set
                    </span>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Progress
                        value={Math.min(100, r.quotaPct)}
                        color={r.quotaPct >= 80 ? 'var(--success)' : r.quotaPct >= 60 ? 'var(--saffron-600)' : 'var(--danger)'}
                        h={4}
                      />
                      <span className="num" style={{ fontSize: 11 }}>{r.quotaPct}%</span>
                    </div>
                  ),
                calls: r.callsMade,
                score: r.score != null ? String(Math.round(r.score)) : '—',
              }))}
            />
          )}
        </>
      )}
    </PageFrame>
  );
}

export default RepsDualPage;
